import React from 'react';
import PropTypes from 'prop-types';
import { NavDropdown, Dropdown, Nav } from 'react-bootstrap';
import { matchPath, withRouter } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Roles } from 'meteor/alanning:roles';
import { get, isNil } from 'lodash';

import {
  isGeoMotionVideo,
  isIAS,
  isMaps,
  isBoilerPlate,
  isLuminarias
} from '../../../modules/checkDeployEnv';
import LinkContainer from '../../containers/LinkContainer';

const iconStyle = { color: '#F1683A' };

const isProject = location => {
  const match = matchPath(location.pathname, {
    path: '/projects/:project_id'
  });

  return !isNil(get(match, 'params.project_id')) && get(match, 'params.project_id') !== 'new';
};

const ProjectsButton = ({ deployEnv }) => {
  if (isIAS(deployEnv) || isGeoMotionVideo(deployEnv)) {
    return (
      <LinkContainer exact to="/projects">
        <Nav.Link>
          <FontAwesomeIcon icon="map" style={iconStyle} /> Projects
        </Nav.Link>
      </LinkContainer>
    );
  }
  return '';
};

const CurrentProjectButton = ({ location }) => {
  const match = matchPath(location.pathname, {
    path: '/projects/:project_id'
  });
  if (isProject(location)) {
    if (match.params.project_id) {
      return (
        <LinkContainer to={`/projects/${match.params.project_id}`}>
          <Nav.Link>
            <FontAwesomeIcon icon="map-marker" style={iconStyle} /> Current Project
          </Nav.Link>
        </LinkContainer>
      );
    }
  }
  return '';
};

const UserMngButton = ({ isAdmin }) => {
  if (isAdmin) {
    return (
      <LinkContainer to="/users">
        <Nav.Link>
          <FontAwesomeIcon icon="users" style={iconStyle} /> User Manager
        </Nav.Link>
      </LinkContainer>
    );
  }
  return '';
};

const UpgradeButton = ({ isPremium, deployEnv }) => {
  if (!isPremium && isIAS(deployEnv)) {
    return (
      <LinkContainer to="/upgrade">
        <Nav.Link>Upgrade</Nav.Link>
      </LinkContainer>
    );
  }
  return '';
};

const AnnotationTemplatesNav = ({ deployEnv }) => {
  if (isGeoMotionVideo(deployEnv))
    return (
      <LinkContainer exact to="/admin/annotationtemplates">
        <NavDropdown.Item eventKey={1.3}>Annotation Templates</NavDropdown.Item>
      </LinkContainer>
    );
  return '';
};

const AdminNav = ({ userId, deployEnv }) =>
  Roles.userIsInRole(userId, 'admin') && (
    <NavDropdown alignRight title="Admin" id="admin-nav-dropdown">
      <LinkContainer exact to="/admin/users">
        <NavDropdown.Item eventKey={1.1}>Users</NavDropdown.Item>
      </LinkContainer>
      <LinkContainer exact to="/admin/users/settings">
        <NavDropdown.Item eventKey={1.2}>User Settings</NavDropdown.Item>
      </LinkContainer>
      <AnnotationTemplatesNav deployEnv={deployEnv} />
    </NavDropdown>
  );
AdminNav.propTypes = {
  deployEnv: PropTypes.string.isRequired,
  userId: PropTypes.string.isRequired
};

const DashboardButton = ({ deployEnv }) => {
  if (isGeoMotionVideo(deployEnv)) {
    return (
      <LinkContainer to="/dashboard">
        <Nav.Link>
          <FontAwesomeIcon icon="globe" style={iconStyle} /> Dashboard
        </Nav.Link>
      </LinkContainer>
    );
  }
  return '';
};

const HangarDropdownTitle = (
  <div className="float-left">
    <FontAwesomeIcon icon="paper-plane" style={iconStyle} /> Hangar
  </div>
);

const HangarNav = ({ deployEnv }) => {
  if (isIAS(deployEnv)) {
    return (
      <NavDropdown alignRight title={HangarDropdownTitle} id="hangar-dropdown">
        <LinkContainer to="/hangar/rpas">
          <NavDropdown.Item eventKey={2.1}>RPAs</NavDropdown.Item>
        </LinkContainer>
        <LinkContainer to="/hangar/payloads">
          <NavDropdown.Item eventKey={2.2}>Payloads</NavDropdown.Item>
        </LinkContainer>
        <LinkContainer to="/hangar/batteries">
          <NavDropdown.Item eventKey={2.3}>Batteries</NavDropdown.Item>
        </LinkContainer>
      </NavDropdown>
    );
  }
  return '';
};

const AtlasButton = ({ deployEnv }) => {
  if (isMaps(deployEnv)) {
    return (
      <LinkContainer exact to="/atlas">
        <Nav.Link>
          <FontAwesomeIcon icon="atlas" style={iconStyle} /> Atlas
        </Nav.Link>
      </LinkContainer>
    );
  }
  return '';
};

const MapsButton = ({ deployEnv }) => {
  if (isMaps(deployEnv)) {
    return (
      <LinkContainer exact to="/maps">
        <Nav.Link>
          <FontAwesomeIcon icon="map" style={iconStyle} /> Maps
        </Nav.Link>
      </LinkContainer>
    );
  }
  return '';
};

const LayersButton = ({ deployEnv }) => {
  if (isMaps(deployEnv)) {
    return (
      <LinkContainer exact to="/layers">
        <Nav.Link>
          <FontAwesomeIcon icon="layer-group" style={iconStyle} /> Layers
        </Nav.Link>
      </LinkContainer>
    );
  }
  return '';
};

const TaskMonitorButton = ({ deployEnv, isFree }) => {
  if (isMaps(deployEnv) && isFree) {
    return (
      <NavDropdown
        title={
          <>
            <FontAwesomeIcon icon="tasks" style={iconStyle} /> Task Monitor
          </>
        }
        id="task-monitor-dropdown"
      >
        <LinkContainer exact to="/taskmonitor">
          <NavDropdown.Item eventKey={1.1}>Task Monitor</NavDropdown.Item>
        </LinkContainer>
        <LinkContainer exact to="/savedtasks">
          <NavDropdown.Item eventKey={1.2}>Saved Tasks</NavDropdown.Item>
        </LinkContainer>
      </NavDropdown>
    );
  }
  return '';
};

const TaskerButton = ({ deployEnv, isFree }) => {
  if (isMaps(deployEnv) && isFree) {
    return (
      <LinkContainer exact to="/itasker">
        <Nav.Link>
          <FontAwesomeIcon icon="project-diagram" style={iconStyle} /> iTasker
        </Nav.Link>
      </LinkContainer>
    );
  }
  return '';
};

const DataButton = ({ deployEnv, isFree }) => {
  if (isMaps(deployEnv) && isFree) {
    return (
      <NavDropdown
        title={
          <>
            <FontAwesomeIcon icon={['far', 'list-alt']} style={iconStyle} /> Data
          </>
        }
        id="connection-dropdown"
      >
        <LinkContainer exact to="/layerServers">
          <NavDropdown.Item eventKey={1.1}>Layer Server</NavDropdown.Item>
        </LinkContainer>
        <LinkContainer exact to="/DBLayerServers">
          <NavDropdown.Item eventKey={1.2}>DB Tile Server</NavDropdown.Item>
        </LinkContainer>
        <LinkContainer exact to="/DBFeatureServers">
          <NavDropdown.Item eventKey={1.3}>DB Feature Server</NavDropdown.Item>
        </LinkContainer>
        <LinkContainer exact to="/DBExternal">
          <NavDropdown.Item eventKey={1.4}>Catalog</NavDropdown.Item>
        </LinkContainer>
        <LinkContainer exact to="/OGC-API-Features">
          <NavDropdown.Item eventKey={1.5}>OGC API Features</NavDropdown.Item>
        </LinkContainer>
        <LinkContainer exact to="/sources">
          <NavDropdown.Item eventKey={1.6}>Sources</NavDropdown.Item>
        </LinkContainer>
        <NavDropdown.Divider />
        <LinkContainer exact to="/connections">
          <NavDropdown.Item eventKey={1.7}>Connections</NavDropdown.Item>
        </LinkContainer>
        <LinkContainer exact to="/videos/profiles">
          <NavDropdown.Item eventKey={1.8}>Profiles Videos</NavDropdown.Item>
        </LinkContainer>
      </NavDropdown>
    );
  }
  return '';
};

const DocumentsButton = ({ deployEnv }) => {
  if (isBoilerPlate(deployEnv)) {
    return (
      <LinkContainer exact to="/documents">
        <Nav.Link>
          <FontAwesomeIcon icon="file-alt" /> Documents
        </Nav.Link>
      </LinkContainer>
    );
  }
  return '';
};

const LuminariasMapButton = ({ deployEnv }) => {
  if (isLuminarias(deployEnv)) {
    return (
      <LinkContainer exact to="/map">
        <Nav.Link>
          <FontAwesomeIcon icon="map" /> Map
        </Nav.Link>
      </LinkContainer>
    );
  }
  return '';
};

const LuminariasReportsButton = ({ deployEnv, userId }) => {
  if (isLuminarias(deployEnv) && Roles.userIsInRole(userId, 'admin')) {
    return (
      <LinkContainer exact to="/reports">
        <Nav.Link>
          <FontAwesomeIcon icon="file-alt" /> Reports
        </Nav.Link>
      </LinkContainer>
    );
  }
  return '';
};

const AuthenticatedNavigation = ({
  isAdmin,
  isPremium,
  isBasic,
  name,
  location,
  deployEnv,
  userId
}) => {
  const notFree = isBasic || isPremium || isAdmin;

  return (
    <>
      <Nav className="mr-auto">
        <ProjectsButton deployEnv={deployEnv} />
        <CurrentProjectButton location={location} />
        <HangarNav deployEnv={deployEnv} />
        <DashboardButton deployEnv={deployEnv} />
        <AtlasButton deployEnv={deployEnv} />
        <MapsButton deployEnv={deployEnv} />
        <LayersButton deployEnv={deployEnv} />
        <DataButton isFree={isBasic || isPremium || isAdmin} deployEnv={deployEnv} />
        <TaskMonitorButton deployEnv={deployEnv} />
        <TaskerButton deployEnv={deployEnv} />
        <DocumentsButton deployEnv={deployEnv} />
        <LuminariasMapButton deployEnv={deployEnv} />
        <LuminariasReportsButton userId={userId} deployEnv={deployEnv} />
        <UserMngButton isAdmin={isAdmin} />
        <AdminNav userId={userId} deployEnv={deployEnv} />
        <UpgradeButton isPremium={isPremium} deployEnv={deployEnv} />
      </Nav>
      <Nav>
        <NavDropdown alignRight title={name} id="user-nav-dropdown">
          {notFree && (
            <>
              <LinkContainer to="/profile">
                <NavDropdown.Item eventKey={3.1}>Profile</NavDropdown.Item>
              </LinkContainer>
              <Dropdown.Divider />
            </>
          )}

          <LinkContainer to="/logout">
            <NavDropdown.Item eventKey={3.2}>Logout</NavDropdown.Item>
          </LinkContainer>
        </NavDropdown>
      </Nav>
    </>
  );
};

AuthenticatedNavigation.propTypes = {
  name: PropTypes.string.isRequired,
  isAdmin: PropTypes.bool.isRequired,
  isPremium: PropTypes.bool.isRequired,
  isBasic: PropTypes.bool.isRequired,
  location: PropTypes.object.isRequired,
  deployEnv: PropTypes.string.isRequired,
  userId: PropTypes.string.isRequired
};

export default withRouter(AuthenticatedNavigation);
